import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles, X, Send, Loader2, Globe } from "lucide-react";
import { api } from "../lib/api";

/**
 * Assistente Q&A flutuante. Aparece em todas as páginas (após login).
 * Mantém histórico de conversa por sessão (sessionStorage por escopo).
 */
export default function AssistantWidget({ escopo, unidadeId, unidadeNome }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const location = useLocation();
  const storageKey = `assist_msgs_${escopo}_${unidadeId || "master"}`;

  const [messages, setMessages] = useState(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  const scrollRef = useRef(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(messages));
    } catch {
      /* quota */
    }
  }, [messages, storageKey]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post(
        "/assistant/chat",
        {
          messages: next,
          contexto_pagina: location.pathname,
          escopo,
          unidade_id: unidadeId || undefined,
        },
        { timeout: 25000 },
      );
      const reply = r.data?.reply || "Sem resposta.";
      const sources = r.data?.sources || [];
      setMessages([
        ...next,
        {
          role: "assistant",
          content: reply,
          grounded: !!r.data?.grounded,
          sources,
        },
      ]);
    } catch (e) {
      setMessages([
        ...next,
        {
          role: "assistant",
          content:
            "⚠️ O assistente está indisponível agora. O resto do sistema continua " +
            "funcionando normal — tente novamente em alguns instantes.",
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    setMessages([]);
    sessionStorage.removeItem(storageKey);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Botão flutuante */}
      {!open && (
        <button
          data-testid="assist-open-btn"
          onClick={() => setOpen(true)}
          aria-label="Abrir assistente"
          className="fixed bottom-5 right-5 z-40 bg-neutral-950 hover:bg-neutral-800 text-white w-14 h-14 rounded-full flex items-center justify-center shadow-2xl border-2 border-yellow-400 transition-transform hover:scale-105"
          title="Pergunte ao Assistente"
        >
          <Sparkles className="w-6 h-6 text-yellow-400" />
        </button>
      )}

      {/* Painel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[min(380px,calc(100vw-1rem))] h-[min(560px,calc(100vh-2.5rem))] bg-white border-4 border-neutral-900 shadow-2xl flex flex-col">
          {/* Header */}
          <div className="bg-neutral-950 text-white p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              <div>
                <div className="font-display font-black text-sm uppercase">
                  Assistente
                </div>
                <div className="font-mono text-[10px] text-neutral-300">
                  {escopo === "master"
                    ? "Painel Master"
                    : unidadeNome || "Unidade"}{" "}
                  · Gemini
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  data-testid="assist-clear-btn"
                  onClick={clear}
                  title="Limpar conversa"
                  className="font-mono text-[10px] uppercase tracking-widest text-neutral-300 hover:text-white px-2 py-1"
                >
                  Limpar
                </button>
              )}
              <button
                data-testid="assist-close-btn"
                onClick={() => setOpen(false)}
                className="hover:bg-white/10 p-1.5"
                aria-label="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Mensagens */}
          <div
            ref={scrollRef}
            data-testid="assist-messages"
            className="flex-1 overflow-y-auto p-3 space-y-2 bg-neutral-50"
          >
            {messages.length === 0 && (
              <div className="text-center py-8 px-2">
                <Sparkles className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
                <p className="font-display font-bold text-sm mb-2">
                  Como posso ajudar?
                </p>
                <p className="font-mono text-[11px] text-neutral-500 leading-relaxed">
                  Pergunte sobre o sistema, regras de negócio,
                  fluxos do PDV, metas, retenção, ou peça resumos.
                </p>
                <div className="mt-4 space-y-1.5 text-left">
                  {[
                    "Como faço para fechar o caixa?",
                    "Como funcionam as metas?",
                    "Quais produtos estão com estoque baixo?",
                  ].map((s) => (
                    <button
                      key={s}
                      onClick={() => setInput(s)}
                      className="block w-full text-left text-xs px-2 py-1.5 border border-neutral-300 hover:bg-neutral-100 font-mono"
                    >
                      → {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`assist-msg-${i}`}
                className={`flex ${
                  m.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-neutral-950 text-white"
                      : "bg-white border-2 border-neutral-900 text-neutral-950"
                  }`}
                >
                  {m.content}
                  {m.grounded && m.sources?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-neutral-200 space-y-1">
                      <div className="font-mono text-[9px] uppercase tracking-widest text-neutral-500 flex items-center gap-1">
                        <Globe className="w-3 h-3" /> Fontes da web
                      </div>
                      {m.sources.slice(0, 5).map((s, idx) => (
                        <a
                          key={idx}
                          href={s.uri}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-[11px] text-blue-700 hover:underline truncate"
                          title={s.title}
                        >
                          {idx + 1}. {s.title}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border-2 border-neutral-900 px-3 py-2 text-sm flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Pensando…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-2 border-t-2 border-neutral-900 bg-white flex gap-2">
            <textarea
              data-testid="assist-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Escreva sua pergunta…"
              rows={1}
              disabled={busy}
              className="flex-1 px-2 py-2 border-2 border-neutral-900 font-mono text-sm resize-none focus:outline-none"
            />
            <button
              data-testid="assist-send-btn"
              onClick={send}
              disabled={busy || !input.trim()}
              className="bg-yellow-400 hover:bg-yellow-500 disabled:opacity-40 text-neutral-950 px-3 border-2 border-neutral-900 flex items-center justify-center"
              aria-label="Enviar"
            >
              <Send className="w-4 h-4" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
